import { graphqlRequest } from '../utils/graphqlRequest';
import { SUPPORT_TICKETS_QUERY } from '../graphql/supportTicket';

const FINANCE_QUERY = `
  query AdminDashFinance {
    getFinanceDashboard {
      totalIncome
      totalExpenses
      pendingPayments
      incomeTrend
      incomeTrendPositive
      expensesTrend
      expensesTrendPositive
      pendingTrend
      pendingTrendPositive
      monthlyData { month income expenses }
      totalGrossPay
      overtimePay
      totalHoursWorked
      totalEmployees
      outstandingBalance
      outstandingClients
      bestMonth
      bestMonthRevenue
    }
  }
`;

const PROJECTS_QUERY = `
  query AdminDashProjects($limit: Int) {
    findAllProject(limit: $limit) {
      id
      name
      status
      startDate
      deadLine
      booking { id fullName estimatedPRice }
      manager { id firstName lastName }
    }
  }
`;

const BOOKINGS_QUERY = `
  query AdminDashBookings {
    findAllBooking(limit: 100, page: 1) {
      total
      data {
        id
        fullName
        email
        status
        estimatedPRice
        createdAt
        stage { id name }
      }
    }
  }
`;

const INVOICES_QUERY = `
  query AdminDashInvoices {
    findAllBookingInvoice {
      id
      invoiceNumber
      paymentStatus
      createdAt
      listTotal
      listServiceCount
    }
  }
`;

const USERS_QUERY = `
  query AdminDashUsers {
    getAllUser(limit: 100, page: 1) {
      total
      data {
        id
        firstName
        lastName
        email
        roles
      }
    }
  }
`;

const ITEMS_QUERY = `
  query AdminDashItems {
    findAllItem {
      id
      name
      quantity
      available_quantity
    }
  }
`;

const PIPELINES_QUERY = `
  query AdminDashPipelines {
    findAllPipeline {
      id
      name
      order
    }
  }
`;

const PIPELINE_STAGES_QUERY = `
  query AdminDashStages($id: String!) {
    findAllStagesOfGivenPipeline(id: $id) {
      id
      name
      order
      booking {
        id
        estimatedPRice
        status
      }
    }
  }
`;

const SERVICES_QUERY = `
  query AdminDashServices {
    getService {
      id
      service
      serviceCount
    }
  }
`;

const SERVICE_RECORDS_QUERY = `
  query AdminDashServiceRecords {
    findAllBookingServiceRecords {
      service { id service }
      booking { id fullName estimatedPRice }
      total
      price
    }
  }
`;

const CALENDAR_EVENTS_QUERY = `
  query AdminDashCalendarEvents($filters: CalendarQueryInput) {
    getCalendarEvents(filters: $filters) {
      id
      title
      type
      date
      description
      assignedTo
    }
  }
`;

const TIME_TRACKING_QUERY = `
  query AdminDashTimeTracking($filter: TimeTrackingFilterInput!) {
    payrollTimeTracking(filter: $filter) {
      totalRegularHours
      totalOvertimeHours
      totalShiftCount
      entries {
        employeeId
        employeeName
        workDate
        regularHours
        overtimeHours
        totalPayableHours
        approvalStatus
      }
    }
  }
`;

const LEAVE_REQUESTS_QUERY = `
  query AdminDashLeaveRequests {
    leaveRequests {
      id
      name
      employeeId
      leaveType
      status
      startDate
      endDate
      appliedDate
    }
  }
`;

const CONTRACTOR_ASSIGNMENTS_QUERY = `
  query AdminDashContractorAssignments {
    getAllContractorAssignments {
      id
      status
      contractorName
      paymentType
      project {
        id
        name
        deadLine
      }
      contractor {
        id
        firstName
        lastName
      }
    }
  }
`;

const INVENTORY_MGMT_QUERY = `
  query AdminDashInventoryMgmt {
    findAllInventoryManagement {
      id
      quantityAssigned
      updateAction
      remark
      tool {
        id
        name
        available_quantity
      }
      project {
        id
        name
      }
    }
  }
`;

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getCurrentWeekRange() {
  const now = new Date();
  const day = now.getDay();
  const diffToMon = day === 0 ? 6 : day - 1;
  const monday = new Date(now);
  monday.setDate(now.getDate() - diffToMon);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { startDate: toYmd(monday), endDate: toYmd(sunday) };
}

function getLast28DaysRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 27);
  return { startDate: toYmd(start), endDate: toYmd(end) };
}

function fmtMeetingTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

const fmtMoney = (n, compact = false) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (compact && v >= 1000) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(v);
  }
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(v);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const norm = (s) => String(s ?? '').toLowerCase().trim();

const normalizeRole = (role) =>
  String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[/\s-]+/g, '_');

const hasAnyRole = (roles = [], expected = []) => {
  const want = expected.map(normalizeRole);
  return (roles || []).some((r) => want.includes(normalizeRole(r)));
};

/** taskStatus values in DB: pending, assigned, approve, cancel */
const isApprovedLead = (status) => {
  const s = norm(status);
  return s === 'approve' || s === 'approved' || s.includes('approve');
};

const isCancelledLead = (status) => {
  const s = norm(status);
  return s === 'cancel' || s === 'cancelled' || s.includes('cancel');
};

const isNewLead = (status) => {
  const s = norm(status);
  return s === 'pending' || s === 'assigned' || s === 'new';
};

const isPaidInvoice = (status) => norm(status) === 'paid';

const isUnpaidInvoice = (status) => {
  const s = norm(status);
  return s === 'unpaid' || s === 'partiallypaid';
};

const projectDisplayStatus = (status) => {
  const s = norm(status);
  if (s === 'complete' || s === 'completed') return 'Completed';
  if (s === 'pending') return 'Pending';
  if (s === 'inactive') return 'Delayed';
  if (s === 'active') return 'Ongoing';
  return status || 'Ongoing';
};

const isOpenTicket = (status) => {
  const s = norm(status);
  return s === 'open' || s === 'in_progress' || s === 'pending';
};

const isResolvedTicket = (status) => {
  const s = norm(status);
  return s === 'resolved' || s === 'closed';
};

const isHighPriority = (priority) => {
  const p = norm(priority);
  return p === 'high' || p === 'urgent' || p === 'critical';
};

function getInvoiceAmount(inv) {
  const listed = Number(inv?.listTotal);
  if (Number.isFinite(listed) && listed > 0) return listed;
  const raw = inv?.rawData;
  if (!raw || typeof raw !== 'object') return 0;
  const amount =
    Number(raw.totalAmount) ||
    Number(raw.services?.totalWithTax) ||
    Number(raw.services?.totalPrice) ||
    0;
  return Number.isFinite(amount) ? amount : 0;
}

const OPTIONAL_LABELS = new Set([
  'items',
  'services',
  'serviceRecords',
  'pipelines',
  'leaveRequests',
  'contractorAssignments',
  'inventoryMgmt',
  'calendar',
  'timeTrackingWeek',
  'timeTrackingMonth',
]);

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function safeRequest(label, fn, failed, { optional = false, retries = 1 } = {}) {
  const isOptional = optional || OPTIONAL_LABELS.has(label) || label.startsWith('pipeline:');
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      if (attempt < retries) {
        await delay(400 * (attempt + 1));
        continue;
      }
    }
  }

  const msg = lastError?.message || String(lastError);
  console.warn(`[adminDashboard] ${label}:`, msg);
  if (!isOptional) {
    failed.push(`${label}: ${msg}`);
  }
  return null;
}

async function runSequential(tasks, failed) {
  const out = {};
  for (const task of tasks) {
    out[task.key] = await safeRequest(task.key, task.run, failed, {
      optional: task.optional,
      retries: task.retries ?? 1,
    });
    if (task.gap) await delay(task.gap);
  }
  return out;
}

function buildLeadOverview(bookings, total) {
  const list = bookings ?? [];
  const newLeads = list.filter((b) => isNewLead(b.status)).length;
  const verified = list.filter((b) => isApprovedLead(b.status)).length;
  const unverified = list.filter((b) => isCancelledLead(b.status)).length;
  const converted = verified;
  const max = Math.max(total || list.length, 1);

  return [
    { label: 'New Leads', value: newLeads, pct: Math.round((newLeads / max) * 100), color: '#3b82f6' },
    { label: 'Verified', value: verified, pct: Math.round((verified / max) * 100), color: '#22c55e' },
    { label: 'Unverified', value: unverified, pct: Math.round((unverified / max) * 100), color: '#eab308' },
    { label: 'Converted', value: converted, pct: Math.round((converted / max) * 100), color: '#14b8a6' },
  ];
}

function currentMonthInvoiceIncome(invoices) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  return (invoices ?? [])
    .filter((inv) => {
      if (!isPaidInvoice(inv.paymentStatus)) return false;
      const d = new Date(inv.createdAt || 0);
      return d.getFullYear() === y && d.getMonth() === m;
    })
    .reduce((sum, inv) => sum + getInvoiceAmount(inv), 0);
}

function buildKpis({ finance, bookings, projects, users, invoices, tickets }) {
  const bookingTotal = bookings?.total ?? bookings?.data?.length ?? 0;
  const bookingData = bookings?.data ?? [];
  const projectList = projects ?? [];
  const activeProjects = projectList.filter((p) => {
    const s = norm(p.status);
    return s === 'active' || s === 'pending';
  }).length;

  const workforceTotal = users?.total ?? users?.data?.length ?? 0;
  const monthIdx = new Date().getMonth();
  let monthIncome =
    finance?.monthlyData?.[monthIdx]?.income ??
    finance?.monthlyData?.find((m) => m.month === MONTH_LABELS[monthIdx])?.income ??
    0;
  if (!monthIncome && invoices?.length) {
    monthIncome = currentMonthInvoiceIncome(invoices);
  }

  const invoiceList = invoices ?? [];
  const pendingInvoices = invoiceList.filter((inv) => isUnpaidInvoice(inv.paymentStatus));
  const partiallyPaid = invoiceList.filter((inv) => norm(inv.paymentStatus) === 'partiallypaid');

  const ticketList = tickets ?? [];
  const openTickets = ticketList.filter((t) => isOpenTicket(t.status));
  const highPriority = openTickets.filter((t) => isHighPriority(t.priority));
  const resolved = ticketList.filter((t) => isResolvedTicket(t.status));
  const slaPct =
    ticketList.length > 0 ? Math.round((resolved.length / ticketList.length) * 1000) / 10 : null;

  const activeClients = bookingData.filter(isApprovedLead).length;

  return [
    {
      id: 'projects',
      label: 'Total Active Projects',
      value: String(activeProjects),
      delta: `${projectList.length} total`,
      tone: 'up',
      icon: 'folder',
    },
    {
      id: 'leads',
      label: 'Total Leads',
      value: String(bookingTotal),
      delta: `${bookingData.filter((b) => !isCancelledLead(b.status)).length} active`,
      tone: 'up',
      icon: 'users',
    },
    {
      id: 'workforce',
      label: 'Total Workforce',
      value: String(workforceTotal),
      delta: finance?.totalEmployees != null ? `${finance.totalEmployees} on payroll` : '—',
      tone: 'up',
      icon: 'hardhat',
    },
    {
      id: 'revenue',
      label: 'Revenue This Month',
      value: fmtMoney(monthIncome, true),
      delta: finance?.incomeTrend ? `${finance.incomeTrend} vs prior` : 'From invoices & finance',
      tone: finance?.incomeTrendPositive === false ? 'warn' : 'up',
      icon: 'dollar',
    },
    {
      id: 'invoices',
      label: 'Pending Invoices',
      value: String(pendingInvoices.length),
      delta: partiallyPaid.length ? `${partiallyPaid.length} partially paid` : 'None partially paid',
      tone: pendingInvoices.length ? 'warn' : 'up',
      icon: 'file',
    },
    {
      id: 'sla',
      label: 'SLA Compliance %',
      value: slaPct != null ? `${slaPct}%` : '—',
      delta: resolved.length ? `${resolved.length} resolved` : 'No ticket data',
      tone: 'up',
      icon: 'shield',
    },
    {
      id: 'tickets',
      label: 'Open Support Tickets',
      value: String(openTickets.length),
      delta: highPriority.length ? `${highPriority.length} high priority` : 'None high priority',
      tone: highPriority.length ? 'warn' : 'up',
      icon: 'ticket',
    },
    {
      id: 'clients',
      label: 'Active Clients',
      value: String(finance?.outstandingClients ?? activeClients),
      delta: finance?.outstandingBalance
        ? `${fmtMoney(finance.outstandingBalance, true)} outstanding`
        : `${activeClients} converted leads`,
      tone: 'up',
      icon: 'building',
    },
  ];
}

function buildPipelineStages(stages) {
  if (!stages?.length) return [];
  return stages
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((stage) => {
      const deals = stage.booking?.length ?? 0;
      const amount = (stage.booking ?? []).reduce((sum, b) => {
        const price = Number(b.estimatedPRice);
        return sum + (Number.isFinite(price) ? price : 0);
      }, 0);
      return {
        stage: stage.name,
        deals,
        amount: fmtMoney(amount, true),
      };
    });
}

function buildActiveProjects(projects) {
  return (projects ?? [])
    .filter((p) => norm(p.status) !== 'complete' && norm(p.status) !== 'completed')
    .slice(0, 8)
    .map((p) => ({
      name: p.name || 'Untitled project',
      client: p.booking?.fullName || '—',
      manager: p.manager
        ? `${p.manager.firstName || ''} ${p.manager.lastName || ''}`.trim() || '—'
        : '—',
      status: projectDisplayStatus(p.status),
      deadline: fmtDate(p.deadLine),
    }));
}

function buildServicePerformance(services, records) {
  const countByService = {};
  const revenueByService = {};
  for (const row of records ?? []) {
    const sid = row?.service?.id;
    if (!sid) continue;
    countByService[sid] = (countByService[sid] || 0) + 1;
    const lineTotal = Number(row.total) || Number(row.price) || Number(row.booking?.estimatedPRice) || 0;
    revenueByService[sid] = (revenueByService[sid] || 0) + lineTotal;
  }

  return (services ?? [])
    .map((s) => ({
      name: s.service || 'Service',
      projects: countByService[s.id] ?? s.serviceCount ?? 0,
      rating: '—',
      revenue: fmtMoney(revenueByService[s.id] || 0, true),
    }))
    .sort((a, b) => b.projects - a.projects)
    .slice(0, 6);
}

function buildLowStock(items) {
  return (items ?? [])
    .filter((it) => Number(it.available_quantity ?? it.quantity) <= 12)
    .sort((a, b) => Number(a.available_quantity) - Number(b.available_quantity))
    .slice(0, 6)
    .map((it) => {
      const qty = Number(it.available_quantity ?? it.quantity);
      return {
        item: it.name,
        units: `${qty} units`,
        level: qty <= 5 ? 'Critical' : 'Low',
      };
    });
}

function buildEquipmentStatus(items, inventoryMgmt) {
  const assignedItemIds = new Set(
    (inventoryMgmt ?? []).map((row) => row?.tool?.id).filter(Boolean),
  );
  const list = items ?? [];
  const total = list.length;
  if (!total) {
    return [
      { label: 'Working', value: 0, tone: 'green' },
      { label: 'Assigned', value: 0, tone: 'amber' },
      { label: 'Low Stock', value: 0, tone: 'red' },
    ];
  }
  const low = list.filter((it) => Number(it.available_quantity) <= 5).length;
  const maintenance = list.filter(
    (it) => Number(it.available_quantity) > 5 && Number(it.available_quantity) <= 12,
  ).length;
  const assigned = list.filter((it) => assignedItemIds.has(it.id)).length;
  const working = Math.max(0, total - low - maintenance);
  return [
    { label: 'Working', value: working, tone: 'green' },
    { label: 'Assigned', value: assigned, tone: 'amber' },
    { label: 'Low Stock', value: low + maintenance, tone: 'red' },
  ];
}

function buildFinanceSummary(finance, invoices) {
  const invoiceList = invoices ?? [];
  const paid = invoiceList.filter((i) => isPaidInvoice(i.paymentStatus)).length;
  const pending = invoiceList.filter((i) => norm(i.paymentStatus) === 'unpaid').length;
  const partial = invoiceList.filter((i) => norm(i.paymentStatus) === 'partiallypaid').length;
  const total = Math.max(invoiceList.length, 1);

  const invoiceIncome = invoiceList
    .filter((i) => isPaidInvoice(i.paymentStatus))
    .reduce((sum, inv) => sum + getInvoiceAmount(inv), 0);

  return {
    revenue: {
      value: fmtMoney(finance?.totalIncome || invoiceIncome, true),
      delta: finance?.incomeTrend || '—',
      up: finance?.incomeTrendPositive !== false,
    },
    expenses: {
      value: fmtMoney(finance?.totalExpenses, true),
      delta: finance?.expensesTrend || '—',
      up: finance?.expensesTrendPositive === true,
    },
    payroll: {
      value: fmtMoney(finance?.totalGrossPay, true),
      note: finance?.pendingPayments
        ? `${fmtMoney(finance.pendingPayments, true)} pending`
        : 'Payroll data',
    },
    due: {
      value: fmtMoney(finance?.outstandingBalance, true),
      note: `${pending + partial} unpaid invoices`,
    },
    invoices: [
      { label: 'Paid', count: paid, pct: Math.round((paid / total) * 100), color: '#22c55e' },
      { label: 'Pending', count: pending, pct: Math.round((pending / total) * 100), color: '#f59e0b' },
      { label: 'Partial', count: partial, pct: Math.round((partial / total) * 100), color: '#f97316' },
      { label: 'Total', count: invoiceList.length, pct: 100, color: '#94a3b8' },
    ],
  };
}

function buildWorkforceMetrics(finance, users, timeTracking, leaveRequests) {
  const total = users?.total ?? users?.data?.length ?? 0;
  const data = users?.data ?? [];
  const fieldWorkers = data.filter((u) =>
    hasAnyRole(u.roles, [
      'FIELD_CLEANER',
      'SCHEDULER_DISPATCHER',
      'SUPERVISOR',
      'CONTRACTOR',
      'OPERATIONS_MANAGER',
    ]),
  ).length;
  const managers = data.filter((u) =>
    hasAnyRole(u.roles, ['OPERATIONS_MANAGER', 'ADMIN', 'SUPER_ADMIN', 'BRANCH_SUPER_ADMIN']),
  ).length;

  const entries = timeTracking?.entries ?? [];
  const presentThisWeek = new Set(entries.map((e) => e.employeeId).filter(Boolean)).size;

  const pendingApproval = entries.filter((e) => norm(e.approvalStatus) === 'pending').length;
  const approvedHours = entries
    .filter((e) => norm(e.approvalStatus) === 'approved')
    .reduce((sum, e) => sum + (Number(e.totalPayableHours) || 0), 0);
  const rejectedHours = entries
    .filter((e) => norm(e.approvalStatus) === 'rejected')
    .reduce((sum, e) => sum + (Number(e.totalPayableHours) || 0), 0);
  const pendingLeave = (leaveRequests ?? []).filter((l) => norm(l.status) === 'pending').length;

  const workersPresent = presentThisWeek;
  const workerPool = fieldWorkers || total;

  return [
    { label: 'Total Workers Present', value: workersPresent, tone: 'purple' },
    { label: 'Absent Workers', value: Math.max(0, workerPool - workersPresent), tone: 'red' },
    { label: 'Pending Approvals', value: pendingApproval, tone: 'amber' },
    { label: 'Active Managers', value: managers, tone: 'green' },
    {
      label: 'Approved Hours',
      value: approvedHours
        ? Math.round(approvedHours)
        : finance?.totalHoursWorked != null
          ? Math.round(finance.totalHoursWorked)
          : 0,
      tone: 'purple',
    },
    { label: 'Rejected Hours', value: rejectedHours ? Math.round(rejectedHours) : 0, tone: 'red' },
    {
      label: 'Overtime Hours',
      value:
        timeTracking?.totalOvertimeHours != null
          ? Math.round(timeTracking.totalOvertimeHours)
          : 0,
      tone: 'amber',
    },
    { label: 'Pending Leave', value: pendingLeave, tone: 'blue' },
  ];
}

function buildWeeklyHours(timeTracking) {
  const dayHours = [0, 0, 0, 0, 0, 0, 0];
  for (const entry of timeTracking?.entries ?? []) {
    const workDate = entry?.workDate;
    if (!workDate) continue;
    const d = new Date(`${workDate}T12:00:00`);
    const dow = d.getDay();
    const idx = dow === 0 ? 6 : dow - 1;
    dayHours[idx] += (Number(entry.regularHours) || 0) + (Number(entry.overtimeHours) || 0);
  }
  return dayHours.map((h) => Math.round(h));
}

function getLastSixMonthSlots(refDate = new Date()) {
  const slots = [];
  for (let offset = 5; offset >= 0; offset -= 1) {
    const d = new Date(refDate.getFullYear(), refDate.getMonth() - offset, 1);
    slots.push({
      year: d.getFullYear(),
      monthIndex: d.getMonth(),
      month: MONTH_LABELS[d.getMonth()],
    });
  }
  return slots;
}

function buildInvoiceIncomeByMonth(invoices) {
  const byKey = {};
  for (const inv of invoices ?? []) {
    if (!inv?.createdAt) continue;
    const amount = getInvoiceAmount(inv);
    if (amount <= 0) continue;
    const d = new Date(inv.createdAt);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    byKey[key] = (byKey[key] || 0) + amount;
  }
  return byKey;
}

function buildRevenueTrend(finance, invoices) {
  const slots = getLastSixMonthSlots();
  const monthly = finance?.monthlyData ?? [];
  const hasFinanceMonths = monthly.length >= 12;
  const currentYear = new Date().getFullYear();
  const invoiceByMonth = buildInvoiceIncomeByMonth(invoices);

  return slots.map((slot) => {
    const invoiceKey = `${slot.year}-${slot.monthIndex}`;
    let income = invoiceByMonth[invoiceKey] || 0;

    if (slot.year === currentYear && hasFinanceMonths) {
      income = Number(monthly[slot.monthIndex]?.income) || 0;
    }

    return {
      month: slot.month,
      value: Math.round(income / 1000) || 0,
      income,
    };
  });
}

function buildProductivityWeeks(timeTracking) {
  const entries = timeTracking?.entries ?? [];
  if (!entries.length) return [];

  const { startDate } = getLast28DaysRange();
  const rangeStart = new Date(`${startDate}T00:00:00`);
  const weekTotals = [0, 0, 0, 0];

  for (const entry of entries) {
    if (!entry?.workDate) continue;
    const workDate = new Date(`${entry.workDate}T12:00:00`);
    const dayOffset = Math.floor((workDate - rangeStart) / 86400000);
    if (dayOffset < 0 || dayOffset > 27) continue;
    const weekIdx = Math.min(3, Math.floor(dayOffset / 7));
    weekTotals[weekIdx] += (Number(entry.regularHours) || 0) + (Number(entry.overtimeHours) || 0);
  }

  return weekTotals.map((value, i) => ({
    week: `Wk ${i + 1}`,
    value: Math.round(value),
  }));
}

function buildUpcomingMeetings(events) {
  const now = Date.now();
  return (events ?? [])
    .filter((ev) => {
      const ts = new Date(ev?.date || 0).getTime();
      return Number.isFinite(ts) && ts >= now - 3600000;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 6)
    .map((ev) => ({
      title: ev.title || 'Event',
      time: fmtMeetingTime(ev.date),
      tag: ev.type || 'Event',
      tone: norm(ev.type) === 'meeting' ? 'green' : norm(ev.type) === 'deadline' ? 'red' : 'blue',
    }));
}

function buildSupportTickets(tickets) {
  const list = tickets ?? [];
  const open = list.filter((t) => isOpenTicket(t.status));
  const high = open.filter((t) => isHighPriority(t.priority));
  const pending = open.filter((t) => norm(t.status) === 'open' || norm(t.status) === 'in_progress');
  return [
    { label: 'Open Tickets', value: open.length, tone: 'blue' },
    { label: 'High Priority', value: high.length, tone: 'red' },
    {
      label: 'Pending Responses',
      value: `${pending.length} tickets`,
      note: pending.length ? 'Requires attention' : undefined,
      tone: 'amber',
    },
  ];
}

function buildSlaMetrics(tickets) {
  const list = tickets ?? [];
  const resolved = list.filter((t) => isResolvedTicket(t.status));
  const breached = openTicketsCount(list);
  return [
    { label: 'SLA Met', value: resolved.length, tone: 'green' },
    { label: 'Open / Breached', value: breached, tone: 'red' },
    { label: 'Avg Response Time', value: '—', tone: 'neutral' },
    { label: 'Escalated Cases', value: highPriorityCount(list), tone: 'amber' },
  ];
}

function openTicketsCount(list) {
  return list.filter((t) => isOpenTicket(t.status)).length;
}

function highPriorityCount(list) {
  return list.filter((t) => isHighPriority(t.priority)).length;
}

function buildActivityTimeline(bookings, invoices, tickets) {
  const events = [];

  for (const b of (bookings?.data ?? []).slice(0, 5)) {
    events.push({
      title: 'Lead activity',
      desc: `${b.fullName || 'Lead'} — ${b.status || 'updated'}`,
      ago: fmtRelative(b.createdAt),
      icon: 'userPlus',
      ts: new Date(b.createdAt || 0).getTime(),
    });
  }
  for (const inv of (invoices ?? []).slice(0, 5)) {
    events.push({
      title: 'Invoice update',
      desc: `Invoice ${inv.invoiceNumber || inv.id} — ${inv.paymentStatus || 'updated'}`,
      ago: fmtRelative(inv.createdAt),
      icon: 'check',
      ts: new Date(inv.createdAt || 0).getTime(),
    });
  }
  for (const t of (tickets ?? []).slice(0, 5)) {
    events.push({
      title: 'Support ticket',
      desc: `#${t.ticketNumber || '—'} ${t.subject || ''}`.trim(),
      ago: fmtRelative(t.createdAt),
      icon: 'ticket',
      ts: new Date(t.createdAt || 0).getTime(),
    });
  }

  return events
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 8)
    .map(({ ts, ...rest }) => rest);
}

function fmtRelative(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(1, mins)} mins ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function buildWorkforceSchedule(users, assignments) {
  const accepted = (assignments ?? []).filter((a) => norm(a.status) === 'accepted');
  if (accepted.length) {
    return accepted.slice(0, 6).map((a) => {
      const contractor =
        a.contractorName ||
        `${a.contractor?.firstName || ''} ${a.contractor?.lastName || ''}`.trim() ||
        'Contractor';
      return {
        name: contractor,
        location: a.project?.name || 'Project assignment',
        shift: a.paymentType || 'Assigned',
        region: fmtDate(a.project?.deadLine),
      };
    });
  }

  return (users?.data ?? [])
    .filter(
      (u) =>
        hasAnyRole(u.roles, [
          'FIELD_CLEANER',
          'SCHEDULER_DISPATCHER',
          'SUPERVISOR',
          'OPERATIONS_MANAGER',
        ]) || hasAnyRole(u.roles, ['ADMIN', 'SUPER_ADMIN']),
    )
    .slice(0, 6)
    .map((u) => ({
      name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Team member',
      location: (u.roles || [])[0] || 'Field',
      shift: 'On roster',
      region: 'Team',
    }));
}

async function loadPrimaryPipelineStages(pipelines, failed) {
  const list = (pipelines ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const pipeline of list) {
    const stages = await safeRequest(`pipeline:${pipeline.name}`, () =>
      graphqlRequest(PIPELINE_STAGES_QUERY, { id: pipeline.id }).then(
        (d) => d?.findAllStagesOfGivenPipeline,
      ),
    failed,
    );
    if (stages?.length) return stages;
  }
  return [];
}

/**
 * Phase 1 — bookings / projects / users only. Clears the main loading spinner.
 */
export async function fetchAdminDashboardCore() {
  const failed = [];
  const gql = (query, variables) =>
    graphqlRequest(query, variables, { timeout: 25000 });

  const bookings = await safeRequest(
    'bookings',
    () => gql(BOOKINGS_QUERY).then((d) => d?.findAllBooking),
    failed,
    { retries: 1 },
  );
  const projects = await safeRequest(
    'projects',
    () => gql(PROJECTS_QUERY, { limit: 50 }).then((d) => d?.findAllProject),
    failed,
    { retries: 1 },
  );
  const users = await safeRequest(
    'users',
    () => gql(USERS_QUERY).then((d) => d?.getAllUser),
    failed,
    { retries: 1 },
  );

  const errors = [];
  const warnings = [];
  if (!bookings && !projects && !users) {
    errors.push(
      'Could not reach the server. Check that the backend is running and you are logged in.',
    );
  } else if (failed.length) {
    warnings.push(
      `${failed.length} core request(s) failed. KPI cards may be incomplete. Try Retry.`,
    );
  }

  const bookingTotal = bookings?.total ?? bookings?.data?.length ?? 0;

  return {
    errors,
    warnings,
    loadWarnings: failed,
    kpiMetrics: buildKpis({
      finance: null,
      bookings,
      projects,
      users,
      invoices: null,
      tickets: null,
    }),
    pipelineStages: [],
    leadOverview: buildLeadOverview(bookings?.data ?? [], bookingTotal),
    activeProjects: buildActiveProjects(projects),
    servicePerformance: [],
    workforceMetrics: buildWorkforceMetrics(null, users, null, null),
    weeklyHours: [0, 0, 0, 0, 0, 0, 0],
    weeklyHourLabels: DAY_LABELS,
    lowStock: [],
    equipmentStatus: [],
    financeSummary: buildFinanceSummary(null, null),
    supportTickets: [],
    slaMetrics: [],
    activityTimeline: buildActivityTimeline(bookings, null, null),
    workforceSchedule: buildWorkforceSchedule(users, null),
    revenueTrend: buildRevenueTrend(null, null),
    productivityWeeks: [],
    upcomingMeetings: [],
    _ctx: { bookings, projects, users, failed },
  };
}

/**
 * Phase 2 — optional / heavy widgets. Merged after the page is already visible.
 */
export async function fetchAdminDashboardSecondary(ctx = {}) {
  const { bookings = null, projects = null, users = null } = ctx;
  const failed = [...(ctx.failed || [])];
  const weekRange = getCurrentWeekRange();
  const monthRange = getLast28DaysRange();
  const gql = (query, variables) =>
    graphqlRequest(query, variables, { timeout: 20000 });

  const finance = await safeRequest(
    'finance',
    () => gql(FINANCE_QUERY).then((d) => d?.getFinanceDashboard),
    failed,
    { optional: true, retries: 0 },
  );
  const invoices = await safeRequest(
    'invoices',
    () => gql(INVOICES_QUERY).then((d) => d?.findAllBookingInvoice),
    failed,
    { optional: true, retries: 0 },
  );
  const tickets = await safeRequest(
    'tickets',
    () => gql(SUPPORT_TICKETS_QUERY).then((d) => d?.supportTickets),
    failed,
    { optional: true, retries: 0 },
  );

  const secondary = await runSequential(
    [
      {
        key: 'pipelines',
        run: () => gql(PIPELINES_QUERY).then((d) => d?.findAllPipeline),
        optional: true,
        retries: 0,
        gap: 50,
      },
      {
        key: 'items',
        run: () => gql(ITEMS_QUERY).then((d) => d?.findAllItem),
        optional: true,
        retries: 0,
        gap: 50,
      },
      {
        key: 'services',
        run: () => gql(SERVICES_QUERY).then((d) => d?.getService),
        optional: true,
        retries: 0,
        gap: 50,
      },
      {
        key: 'serviceRecords',
        run: () =>
          gql(SERVICE_RECORDS_QUERY).then((d) => d?.findAllBookingServiceRecords),
        optional: true,
        retries: 0,
        gap: 50,
      },
      {
        key: 'leaveRequests',
        run: () => gql(LEAVE_REQUESTS_QUERY).then((d) => d?.leaveRequests),
        optional: true,
        retries: 0,
        gap: 50,
      },
      {
        key: 'contractorAssignments',
        run: () =>
          gql(CONTRACTOR_ASSIGNMENTS_QUERY).then(
            (d) => d?.getAllContractorAssignments,
          ),
        optional: true,
        retries: 0,
        gap: 50,
      },
      {
        key: 'inventoryMgmt',
        run: () =>
          gql(INVENTORY_MGMT_QUERY).then((d) => d?.findAllInventoryManagement),
        optional: true,
        retries: 0,
        gap: 50,
      },
      {
        key: 'calendar',
        run: () =>
          gql(CALENDAR_EVENTS_QUERY, { filters: {} }).then(
            (d) => d?.getCalendarEvents,
          ),
        optional: true,
        retries: 0,
        gap: 50,
      },
      {
        key: 'timeTrackingWeek',
        run: () =>
          gql(TIME_TRACKING_QUERY, { filter: weekRange }).then(
            (d) => d?.payrollTimeTracking,
          ),
        optional: true,
        retries: 0,
        gap: 50,
      },
      {
        key: 'timeTrackingMonth',
        run: () =>
          gql(TIME_TRACKING_QUERY, { filter: monthRange }).then(
            (d) => d?.payrollTimeTracking,
          ),
        optional: true,
        retries: 0,
      },
    ],
    failed,
  );

  const {
    pipelines,
    items,
    services,
    serviceRecords,
    leaveRequests,
    contractorAssignments,
    inventoryMgmt,
    calendarEvents,
    timeTrackingWeek,
    timeTrackingMonth,
  } = secondary;

  const pipelineStages = await loadPrimaryPipelineStages(pipelines, failed);

  const warnings = [];
  if (failed.length) {
    warnings.push(
      `${failed.length} optional section(s) failed or timed out. Some widgets may be empty.`,
    );
  }

  return {
    warnings,
    loadWarnings: failed,
    kpiMetrics: buildKpis({
      finance,
      bookings,
      projects,
      users,
      invoices,
      tickets,
    }),
    pipelineStages: buildPipelineStages(pipelineStages),
    servicePerformance: buildServicePerformance(services, serviceRecords),
    workforceMetrics: buildWorkforceMetrics(
      finance,
      users,
      timeTrackingWeek,
      leaveRequests,
    ),
    weeklyHours: buildWeeklyHours(timeTrackingWeek),
    weeklyHourLabels: DAY_LABELS,
    lowStock: buildLowStock(items),
    equipmentStatus: buildEquipmentStatus(items, inventoryMgmt),
    financeSummary: buildFinanceSummary(finance, invoices),
    supportTickets: buildSupportTickets(tickets),
    slaMetrics: buildSlaMetrics(tickets),
    activityTimeline: buildActivityTimeline(bookings, invoices, tickets),
    workforceSchedule: buildWorkforceSchedule(users, contractorAssignments),
    revenueTrend: buildRevenueTrend(finance, invoices),
    productivityWeeks: buildProductivityWeeks(timeTrackingMonth),
    upcomingMeetings: buildUpcomingMeetings(calendarEvents),
  };
}

/**
 * Full load (core then secondary). Prefer progressive core + secondary in the UI.
 */
export async function fetchAdminDashboardData() {
  const core = await fetchAdminDashboardCore();
  if (core.errors?.length) return core;
  const secondary = await fetchAdminDashboardSecondary(core._ctx);
  const { _ctx, ...coreRest } = core;
  return {
    ...coreRest,
    ...secondary,
    errors: core.errors || [],
    warnings: [...(core.warnings || []), ...(secondary.warnings || [])],
    loadWarnings: [
      ...(core.loadWarnings || []),
      ...(secondary.loadWarnings || []),
    ],
    leadOverview: core.leadOverview,
    activeProjects: core.activeProjects,
  };
}
